/**
 * A route-level boundary per route, plus one at the shell.
 *
 * It shows what failed, the request id so a user-reported failure is findable in the server log,
 * and a retry that refetches rather than reloading the page. The prototype has none: an exception
 * inside its render leaves the page blank or half-written.
 */

import { useQueryClient } from '@tanstack/react-query';
import { isRouteErrorResponse, useRouteError } from 'react-router';
import { ApiError, problemsOf, requestIdOf } from '~/api/client';
import { Button } from '~/ui/Button';
import { Callout } from '~/ui/Feedback';

export function RouteError() {
  const error = useRouteError();
  const client = useQueryClient();

  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <Callout tone="warn" title="That is not a screen this engine has">
        The address you followed does not match anything the application serves. The link may be
        from an older version, or a code in it may have been mistyped.
      </Callout>
    );
  }

  const problems = problemsOf(error);
  const requestId = requestIdOf(error);
  const code = error instanceof ApiError ? error.code : null;

  return (
    <Callout tone="issue" title="This screen could not be shown" alert>
      <ul>
        {problems.map((problem) => (
          <li key={problem}>{problem}</li>
        ))}
      </ul>
      {code ? <p>Reported as: {code}</p> : null}
      {requestId ? <p>Request id: {requestId}</p> : null}
      <p style={{ marginTop: 12 }}>
        <Button
          onClick={() => {
            void client.refetchQueries();
          }}
        >
          Try again
        </Button>
      </p>
    </Callout>
  );
}

export function QueryError({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const problems = problemsOf(error);
  const requestId = requestIdOf(error);

  return (
    <Callout tone="issue" title="The engine could not answer" alert>
      <ul>
        {problems.map((problem) => (
          <li key={problem}>{problem}</li>
        ))}
      </ul>
      {requestId ? <p>Request id: {requestId}</p> : null}
      {onRetry ? (
        <p style={{ marginTop: 12 }}>
          <Button onClick={onRetry}>Try again</Button>
        </p>
      ) : null}
    </Callout>
  );
}
